import React, { useState } from 'react';
import { afipService } from '../services/api';
import './AfipCertificates.css';

function AfipCertificates() {
  const [cuit, setCuit] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [certStatus, setCertStatus] = useState(null);

  const cleanCuit = (value) => value.replace(/[^0-9]/g, '');

  const handleCheckStatus = async () => {
    const cleanedCuit = cleanCuit(cuit);
    if (cleanedCuit.length < 10 || cleanedCuit.length > 11) {
      setError('Ingresá un CUIT válido (10 u 11 dígitos) para consultar.');
      return;
    }
    setError('');
    setSuccess('');
    setCertStatus(null);
    setChecking(true);
    try {
      const data = await afipService.getAfipCertStatus(cleanedCuit);
      setCertStatus(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al consultar el estado de los certificados.');
    } finally {
      setChecking(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const cleanedCuit = cleanCuit(cuit);
    if (cleanedCuit.length < 10 || cleanedCuit.length > 11) {
      setError('El CUIT debe tener 10 u 11 dígitos (sin guiones).');
      return;
    }
    if (!certFile) {
      setError('Seleccioná el archivo del certificado (.crt o .pem).');
      return;
    }
    if (!keyFile) {
      setError('Seleccioná el archivo de la clave privada (.key o .pem).');
      return;
    }

    try {
      setUploading(true);

      const { certUploadUrl, keyUploadUrl } = await afipService.getAfipCertUploadUrl(cleanedCuit);

      await Promise.all([
        afipService.uploadCertFile(certUploadUrl, certFile),
        afipService.uploadCertFile(keyUploadUrl, keyFile),
      ]);

      setSuccess(
        `Certificados cargados correctamente para CUIT ${cleanedCuit}. ` +
        'El sistema los usará en la próxima validación AFIP.'
      );
      setCertFile(null);
      setKeyFile(null);
      document.getElementById('certFileInput').value = '';
      document.getElementById('keyFileInput').value = '';

      // Refrescar estado automáticamente
      const data = await afipService.getAfipCertStatus(cleanedCuit);
      setCertStatus(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al subir los certificados.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (isoStr) => {
    return new Date(isoStr).toLocaleString('es-AR');
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>Certificados AFIP</h1>
      </div>

      <div className="card">
        <h2>¿Para qué sirve esto?</h2>
        <p className="cert-description">
          Para validar comprobantes con AFIP el sistema necesita un certificado digital y su clave privada
          correspondiente, emitidos por AFIP para cada empresa (CUIT). Subí aquí los archivos PEM generados
          para el entorno de <strong>homologación</strong> (pruebas) o producción.
        </p>
        <div className="cert-paths-info">
          <p><strong>Ruta esperada en S3:</strong></p>
          <code>{`{cuit}/certificado.crt`}</code> — certificado emitido por AFIP (PEM)<br />
          <code>{`{cuit}/privada.key`}</code> — clave privada sin passphrase (PEM)
        </div>
      </div>

      <div className="card">
        <h2>Consultar certificados cargados</h2>

        <div className="cert-check-row">
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <input
              type="text"
              className="form-control"
              placeholder="CUIT de la empresa (ej: 20123456789)"
              value={cuit}
              onChange={(e) => {
                setCuit(e.target.value);
                setCertStatus(null);
              }}
              maxLength={13}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCheckStatus}
            disabled={checking}
          >
            {checking ? 'Consultando...' : 'Ver estado'}
          </button>
        </div>

        {certStatus && (
          <div className="cert-status-table">
            <table className="table" style={{ marginTop: '16px' }}>
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Estado</th>
                  <th>Tamaño</th>
                  <th>Última modificación</th>
                </tr>
              </thead>
              <tbody>
                {certStatus.files.map((f) => (
                  <tr key={f.key}>
                    <td>
                      <code>{f.key}</code><br />
                      <small style={{ color: '#888' }}>{f.label}</small>
                    </td>
                    <td>
                      {f.exists
                        ? <span className="badge badge-afip-valid">✓ Cargado</span>
                        : <span className="badge badge-afip-invalid">✗ No encontrado</span>}
                    </td>
                    <td>{f.exists ? formatBytes(f.size) : '—'}</td>
                    <td>{f.exists ? formatDate(f.lastModified) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Cargar / Reemplazar Certificados</h2>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleUpload}>
          <div className="form-group">
            <label>CUIT de la empresa</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: 20123456789"
              value={cuit}
              onChange={(e) => setCuit(e.target.value)}
              maxLength={13}
              required
            />
            <small className="form-hint">Ingresá solo números (sin guiones).</small>
          </div>

          <div className="form-group">
            <label>Certificado (.crt o .pem)</label>
            <input
              id="certFileInput"
              type="file"
              className="form-control"
              accept=".crt,.pem,.cer"
              onChange={(e) => setCertFile(e.target.files[0] || null)}
              required
            />
            <small className="form-hint">
              Archivo de certificado en formato PEM, emitido por AFIP.
            </small>
          </div>

          <div className="form-group">
            <label>Clave privada (.key o .pem)</label>
            <input
              id="keyFileInput"
              type="file"
              className="form-control"
              accept=".key,.pem"
              onChange={(e) => setKeyFile(e.target.files[0] || null)}
              required
            />
            <small className="form-hint">
              Clave privada sin passphrase en formato PEM. No se almacena en la aplicación.
            </small>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={uploading}
            >
              {uploading ? 'Subiendo certificados...' : 'Subir Certificados'}
            </button>
          </div>
        </form>
      </div>

      <div className="card cert-warning-card">
        <h2>⚠ Consideraciones de seguridad</h2>
        <ul className="cert-warning-list">
          <li>Los archivos se almacenan cifrados en S3 (AES-256) con acceso restringido a los Lambdas del sistema.</li>
          <li>Nunca compartas la clave privada por canales inseguros.</li>
          <li>Los certificados AFIP tienen vencimiento — renovalos antes de que expiren para evitar errores de validación.</li>
          <li>Una vez subidos, los archivos anteriores son sobreescritos para el mismo CUIT.</li>
        </ul>
      </div>
    </div>
  );
}

export default AfipCertificates;
